/**
 * Content bundle validation (spec section 13). Allowed content is declarative JSON plus restricted
 * Markdown / template syntax with a versioned variable schema. Executable JavaScript, shell
 * snippets, unrestricted HTML, remote includes and expression evaluation are rejected.
 */
import { z } from 'zod';
import { ContentBundleItemSchema, ContentBundleSchema, type ContentBundle, type ContentBundleItem } from '../api/content.js';
import { TEMPLATE_TYPES, type TemplateType } from '../api/entities.js';
import { CanonicalKeySchema, TemplateIdSchema } from '../ids.js';
import { AGENDA_SECTION_IDS, AGENDA_TABS, ASK_GROUPS, ClassificationSchema, TaskGroupCategorySchema } from '../providers/records.js';
import { digestJson } from './hash.js';

// ---------------------------------------------------------------------------------------------
// Variable schema versions
// ---------------------------------------------------------------------------------------------

export const VARIABLE_SCHEMA_VERSIONS = {
  email_template: {
    1: ['client_name', 'company', 'task_name', 'employee_count', 'employee_list'],
    /** v2 (template feedback 2026-09-25): the analyst's name for Dakota's first-person opening and the tenant onboarding page. */
    2: ['client_name', 'company', 'task_name', 'employee_count', 'employee_list', 'analyst_name', 'onboarding_url'],
  },
  agenda_template: {
    1: ['account_name', 'meeting_date', 'snapshot_date', 'timezone', 'provider', 'framework_status', 'customer_asks'],
    /** v2 (template feedback 2026-09-25): the long meeting date and the linked report sheets. */
    2: ['account_name', 'meeting_date', 'meeting_date_long', 'snapshot_date', 'timezone', 'provider', 'framework_status', 'customer_asks', 'sheet_links'],
  },
  remediation_ask: {
    1: ['children', 'count'],
  },
} as const satisfies Partial<Record<TemplateType, Record<number, readonly string[]>>>;

export type VariableSchemaType = keyof typeof VARIABLE_SCHEMA_VERSIONS;

export function variablesFor(type: VariableSchemaType, version: number): readonly string[] | null {
  const versions = VARIABLE_SCHEMA_VERSIONS[type] as Record<number, readonly string[]>;
  return versions[version] ?? null;
}

export function latestVariableSchemaVersion(type: VariableSchemaType): number {
  return Math.max(...Object.keys(VARIABLE_SCHEMA_VERSIONS[type]).map(Number));
}

// ---------------------------------------------------------------------------------------------
// Body schemas per template type
// ---------------------------------------------------------------------------------------------

const restrictedText = (max: number) => z.string().max(max);

export const AgendaSectionTemplateSchema = z.object({
  id: z.enum(AGENDA_SECTION_IDS),
  heading: restrictedText(200),
  /** Managed sections are regenerated; operator sections keep the existing document text. */
  managed: z.boolean(),
  /** Restricted Markdown inserted when the document is created or the section is empty. */
  defaultMarkdown: restrictedText(20_000).default(''),
  /** Document tab the section lives on (template feedback 2026-09-25). */
  tab: z.enum(AGENDA_TABS).default('agenda'),
  /** Operator-owned italic line written under the heading on create only. */
  intro: restrictedText(500).nullable().default(null),
});

export const AGENDA_HEADING_STYLES = ['heading', 'bold'] as const;
export const AGENDA_CHILDREN_STYLES = ['inline', 'nested'] as const;
export const AGENDA_GROUP_LABEL_STYLES = ['heading', 'italic'] as const;
export const AGENDA_PROVIDER_REMEDIATION_MODES = ['raw', 'sanitized', 'never'] as const;

/**
 * Agenda template body. Every field added by the template feedback of 2026-09-25 has a default
 * that reproduces the v1 rendering, so a v1 body stays valid and unchanged.
 */
export const AgendaTemplateBodySchema = z.object({
  variablesSchemaVersion: z.number().int().positive(),
  /** Google Docs file title, e.g. "[{account_name}] vCISO Rolling Agenda"; also the H1 in the legacy heading style. */
  title: restrictedText(300),
  /** Bold lines at the top of the document ("{account_name} & Agency", "Bi-Weekly Status Call Rolling Meeting Agenda"). */
  headerLines: z.array(restrictedText(200)).max(4).default([]),
  /** Plain separator paragraph under the header (Dakota's underscore rule); null for none. */
  separator: restrictedText(200).nullable().default(null),
  /** Bold meeting line, e.g. "{meeting_date_long} | Asynchronous Update"; null renders the legacy "Meeting date: …" line. */
  meetingLine: restrictedText(300).nullable().default(null),
  /** `bold` renders section headings as bold paragraphs (Dakota's docs); `heading` uses Docs heading styles. */
  headingStyle: z.enum(AGENDA_HEADING_STYLES).default('heading'),
  sections: z.array(AgendaSectionTemplateSchema).min(1).max(20),
  /** Line template for one ask; supports {title}, {ask}, {status}. */
  askLineTemplate: restrictedText(500).default('- {title}: {ask}'),
  /** `nested` lists every child as a sub-bullet under the ask; `inline` substitutes the comma list into the text. */
  childrenStyle: z.enum(AGENDA_CHILDREN_STYLES).default('inline'),
  groupHeadings: z.partialRecord(z.enum(ASK_GROUPS), restrictedText(120)),
  groupLabelStyle: z.enum(AGENDA_GROUP_LABEL_STYLES).default('heading'),
  /** Asks kept on the agenda tab per group; the rest go to the Additional asks tab. */
  groupLimits: z.partialRecord(z.enum(ASK_GROUPS), z.number().int().min(1).max(500)).default({}),
  overflowHeading: restrictedText(200).default('Additional asks'),
  /** Italic note under a capped group; supports {count}, {group}, {tab}. */
  overflowNote: restrictedText(500).default('The remaining {count} {group} asks are listed on the {tab} tab.'),
  /** Line at the top of the asks region pointing at the linked sheets; supports {sheet_links}. Omitted when no sheet is linked. */
  sheetsLine: restrictedText(500).nullable().default(null),
  /** Children listed per ask; null lists them all. */
  childLimit: z.number().int().min(1).max(100).nullable().default(5),
  /**
   * Document layout (template feedback 2026-09-29): font for the whole agenda, a centred header
   * block, the separator drawn as a full-width rule under the header instead of underscore text,
   * and space above each section heading and group label. Paragraph styles only, so the text of
   * managed regions (and every update diff) is unchanged. The defaults reproduce the v1 rendering.
   */
  layout: z
    .object({
      fontFamily: z.string().trim().min(1).max(60).nullable().default(null),
      centerHeader: z.boolean().default(false),
      separatorAsRule: z.boolean().default(false),
      sectionSpacingPt: z.number().min(0).max(36).default(0),
    })
    .default({ fontFamily: null, centerHeader: false, separatorAsRule: false, sectionSpacingPt: 0 }),
  emptyAsksText: restrictedText(500).default('No open asks in this snapshot.'),
  /** Deterministic line for a record without ask text, per object type; supports {title}. */
  missingAskFallback: z.object({ document: restrictedText(300), policy: restrictedText(300), default: restrictedText(300) }).default({ document: '{title}', policy: '{title}', default: '{title}' }),
  /** How provider remediation text is used when no Agency ask exists: as is, sanitized to one clean line, or never. */
  providerRemediation: z.enum(AGENDA_PROVIDER_REMEDIATION_MODES).default('raw'),
  tabTitles: z.object({ agenda: restrictedText(100), history: restrictedText(100), overflow: restrictedText(100) }).default({ agenda: 'Agenda', history: 'Previously completed sprints', overflow: 'Additional asks' }),
});
export type AgendaTemplateBody = z.infer<typeof AgendaTemplateBodySchema>;

export const EmailTemplateBodySchema = z.object({
  variablesSchemaVersion: z.number().int().positive(),
  name: restrictedText(160),
  category: TaskGroupCategorySchema.nullable(),
  subject: restrictedText(500),
  body: restrictedText(20_000),
});
export type EmailTemplateBody = z.infer<typeof EmailTemplateBodySchema>;

export const RemediationAskBodySchema = z.object({
  variablesSchemaVersion: z.number().int().positive(),
  canonicalKey: CanonicalKeySchema,
  displayName: restrictedText(300),
  text: restrictedText(20_000),
});
export type RemediationAskBody = z.infer<typeof RemediationAskBodySchema>;

export const ClassificationRuleBodySchema = z.object({
  canonicalKey: CanonicalKeySchema,
  displayName: restrictedText(300),
  displayDescription: restrictedText(20_000).nullable(),
  classification: ClassificationSchema,
  customAskNeeded: z.boolean(),
});
export type ClassificationRuleBody = z.infer<typeof ClassificationRuleBodySchema>;

export const MatchingRuleBodySchema = z.object({
  provider: z.enum(['vanta', 'drata']),
  version: z.number().int().positive(),
  rules: z
    .array(
      z.object({
        id: restrictedText(120),
        taskGroupCategory: TaskGroupCategorySchema,
        /** Anchored, case-insensitive regex source without lookaround (validated separately). */
        namePattern: restrictedText(300),
        deviceTask: z.boolean(),
      }),
    )
    .max(200),
});
export type MatchingRuleBody = z.infer<typeof MatchingRuleBodySchema>;

export const ReportColumnsBodySchema = z.object({
  report: z.enum(['infrastructure', 'employee_compliance', 'vulnerability_summary', 'vulnerability_detail', 'sources']),
  columns: z
    .array(
      z.object({
        key: z.string().regex(/^[a-z][a-zA-Z0-9_.]{0,63}$/),
        label: restrictedText(120),
        width: z.number().int().min(40).max(1200).optional(),
        hidden: z.boolean().default(false),
        wrap: z.boolean().default(false),
      }),
    )
    .min(1)
    .max(64),
});
export type ReportColumnsBody = z.infer<typeof ReportColumnsBodySchema>;

export const HelpContentBodySchema = z.object({
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,127}$/),
  title: restrictedText(200),
  section: z.enum(['setup', 'workflows', 'field_mappings', 'shortcuts', 'storage_retention', 'errors', 'release_notes', 'support']),
  markdown: restrictedText(200_000),
  order: z.number().int().nonnegative(),
});
export type HelpContentBody = z.infer<typeof HelpContentBodySchema>;

export const FeatureFlagsBodySchema = z.object({
  flags: z.record(z.string().regex(/^[a-z][a-z0-9_]{1,63}$/), z.union([z.boolean(), z.number(), z.string().max(200)])),
  rumiDisabledAccountIds: z.array(z.string()),
});
export type FeatureFlagsBody = z.infer<typeof FeatureFlagsBodySchema>;

export const ModelConfigBodySchema = z.object({
  defaultModel: z.string().min(1).max(128),
  higherCapabilityModel: z.string().max(128).nullable(),
  permittedModels: z.array(z.string().min(1).max(128)).min(1),
  maxOutputTokens: z.number().int().positive().max(200_000),
  reasoningEffort: z.enum(['minimal', 'low', 'medium', 'high']).nullable(),
  temperature: z.number().min(0).max(2).nullable(),
  priceTable: z.object({
    version: z.string().min(1).max(64),
    effectiveFrom: z.iso.datetime(),
    models: z.record(z.string(), z.object({ inputMicroUsdPerMillion: z.number().int().nonnegative(), cachedInputMicroUsdPerMillion: z.number().int().nonnegative(), outputMicroUsdPerMillion: z.number().int().nonnegative() })),
  }),
  perUserBudgetMicroUsdPerMonth: z.number().int().nonnegative().nullable(),
  maxReservationMicroUsd: z.number().int().positive(),
  baseUrl: z.string().url(),
  policyVersion: z.string().min(1).max(64),
});
export type ModelConfigBody = z.infer<typeof ModelConfigBodySchema>;

const bucket = z.object({ requestsPerMinute: z.number().int().positive().max(10_000), burst: z.number().int().positive().max(10_000), concurrency: z.number().int().positive().max(64) });

/** Tuning stays within validated bounds: Vanta ≤ 50/min documented, Drata ≤ 500/min per IP. */
export const RateLimitConfigBodySchema = z
  .object({
    providers: z.partialRecord(
      z.enum(['vanta', 'drata']),
      z.object({ ceiling: bucket, auth: bucket, perEndpointFamily: z.record(z.string(), bucket), egressGroupCeilingPerMinute: z.number().int().positive().nullable() }),
    ),
    retry: z.object({
      maxRetries: z.number().int().min(0).max(10),
      perRequestBudgetMs: z.number().int().positive().max(60 * 60 * 1000),
      baseDelayMs: z.number().int().positive().max(60_000),
      maxDelayMs: z.number().int().positive().max(30 * 60 * 1000),
      connectTimeoutMs: z.number().int().positive().max(120_000),
      responseTimeoutMs: z.number().int().positive().max(600_000),
    }),
  })
  .superRefine((body, ctx) => {
    const vanta = body.providers.vanta;
    if (vanta && vanta.ceiling.requestsPerMinute > 50) ctx.addIssue({ code: 'custom', path: ['providers', 'vanta', 'ceiling', 'requestsPerMinute'], message: 'Vanta documents 50 requests/minute' });
    if (vanta && vanta.auth.requestsPerMinute > 5) ctx.addIssue({ code: 'custom', path: ['providers', 'vanta', 'auth', 'requestsPerMinute'], message: 'Vanta documents 5 OAuth requests/minute' });
    const drata = body.providers.drata;
    if (drata && drata.ceiling.requestsPerMinute > 500) ctx.addIssue({ code: 'custom', path: ['providers', 'drata', 'ceiling', 'requestsPerMinute'], message: 'Drata documents 500 requests/minute per source IP' });
  });
export type RateLimitConfigBody = z.infer<typeof RateLimitConfigBodySchema>;

export const TEMPLATE_BODY_SCHEMAS: Record<TemplateType, z.ZodType> = {
  agenda_template: AgendaTemplateBodySchema,
  email_template: EmailTemplateBodySchema,
  remediation_ask: RemediationAskBodySchema,
  classification_rule: ClassificationRuleBodySchema,
  matching_rule: MatchingRuleBodySchema,
  report_columns: ReportColumnsBodySchema,
  help_content: HelpContentBodySchema,
  feature_flags: FeatureFlagsBodySchema,
  model_config: ModelConfigBodySchema,
  rate_limit_config: RateLimitConfigBodySchema,
};

/** Current body schema version per type; bump when a body schema changes incompatibly. */
export const TEMPLATE_BODY_SCHEMA_VERSION: Record<TemplateType, number> = Object.fromEntries(TEMPLATE_TYPES.map((t) => [t, 1])) as Record<TemplateType, number>;

// ---------------------------------------------------------------------------------------------
// Content safety
// ---------------------------------------------------------------------------------------------

export type ContentIssueCode =
  | 'schema'
  | 'executable_script'
  | 'html_not_allowed'
  | 'javascript_url'
  | 'data_url'
  | 'remote_include'
  | 'remote_image'
  | 'expression_evaluation'
  | 'shell_snippet'
  | 'insecure_link'
  | 'unknown_variable'
  | 'variable_schema_version'
  | 'invalid_pattern'
  | 'digest_mismatch'
  | 'manifest_mismatch';

export interface ContentIssue {
  code: ContentIssueCode;
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ContentValidation {
  ok: boolean;
  issues: ContentIssue[];
}

interface ForbiddenPattern {
  code: ContentIssueCode;
  pattern: RegExp;
  message: string;
}

/** Patterns rejected in any string of any template body. */
export const FORBIDDEN_CONTENT_PATTERNS: readonly ForbiddenPattern[] = [
  { code: 'executable_script', pattern: /<\s*script\b/i, message: 'script elements are not allowed' },
  { code: 'javascript_url', pattern: /javascript\s*:/i, message: 'javascript: URLs are not allowed' },
  { code: 'javascript_url', pattern: /vbscript\s*:/i, message: 'vbscript: URLs are not allowed' },
  { code: 'data_url', pattern: /\bdata\s*:\s*[a-z]+\/[a-z0-9.+-]+\s*[;,]/i, message: 'data: URLs are not allowed' },
  { code: 'html_not_allowed', pattern: /<\s*\/?\s*(iframe|object|embed|form|input|button|link|meta|style|svg|math|base|frame|frameset|applet|template|slot)\b/i, message: 'this HTML element is not allowed' },
  { code: 'html_not_allowed', pattern: /<\s*[a-z][a-z0-9-]*\b[^>]*\bon[a-z]+\s*=/i, message: 'inline event handlers are not allowed' },
  { code: 'html_not_allowed', pattern: /<\s*[a-z][a-z0-9-]*(\s+[^>]*)?>/i, message: 'raw HTML is not allowed; use restricted Markdown' },
  { code: 'remote_include', pattern: /\{\{\s*>?\s*include\b/i, message: 'includes are not allowed' },
  { code: 'remote_include', pattern: /<!--\s*#include\b/i, message: 'server-side includes are not allowed' },
  { code: 'remote_include', pattern: /@import\b/i, message: '@import is not allowed' },
  { code: 'remote_include', pattern: /\burl\s*\(\s*['"]?\s*https?:/i, message: 'remote url() includes are not allowed' },
  { code: 'remote_image', pattern: /!\[[^\]]*\]\(\s*(https?:|\/\/)/i, message: 'remote images are not allowed' },
  { code: 'expression_evaluation', pattern: /\{\{[\s\S]*?\}\}/, message: 'double-brace expressions are not allowed' },
  { code: 'expression_evaluation', pattern: /\$\{[\s\S]*?\}/, message: 'template-literal expressions are not allowed' },
  { code: 'expression_evaluation', pattern: /<%[\s\S]*?%>/, message: 'server template tags are not allowed' },
  { code: 'expression_evaluation', pattern: /\{%[\s\S]*?%\}/, message: 'template block tags are not allowed' },
  { code: 'expression_evaluation', pattern: /\b(eval|Function|setTimeout|setInterval|require|import)\s*\(/, message: 'code evaluation is not allowed' },
  { code: 'shell_snippet', pattern: /^\s*#!\s*\/(usr\/)?(bin|local)\//m, message: 'shell shebangs are not allowed' },
  { code: 'shell_snippet', pattern: /\$\(\s*[a-z]/i, message: 'shell command substitution is not allowed' },
  { code: 'shell_snippet', pattern: /\b(curl|wget|bash|sh|zsh|powershell|cmd\.exe|osascript)\s+(-[a-zA-Z]+\s+)*(https?:|\/|~|\$)/i, message: 'shell commands are not allowed' },
  { code: 'shell_snippet', pattern: /`[^`\n]*\b(rm|sudo|chmod|curl|wget|bash|sh)\b[^`\n]*`/i, message: 'shell snippets are not allowed' },
];

const VARIABLE_PATTERN = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
const MARKDOWN_LINK = /\]\(\s*([^)\s]+)/g;

function walkStrings(value: unknown, path: string, visit: (text: string, path: string) => void): void {
  if (typeof value === 'string') visit(value, path);
  else if (Array.isArray(value)) value.forEach((v, i) => walkStrings(v, `${path}[${i}]`, visit));
  else if (value && typeof value === 'object') for (const [k, v] of Object.entries(value as Record<string, unknown>)) walkStrings(v, path ? `${path}.${k}` : k, visit);
}

/** Fields whose {tokens} are template variables (checked against the variable schema). */
const VARIABLE_FIELDS: Partial<Record<TemplateType, readonly string[]>> = {
  email_template: ['subject', 'body'],
  agenda_template: ['title', 'headerLines', 'meetingLine', 'sheetsLine', 'askLineTemplate', 'emptyAsksText', 'overflowHeading', 'overflowNote', 'missingAskFallback', 'sections'],
  remediation_ask: ['text'],
};

const ASK_LINE_TOKENS = ['title', 'ask', 'status'];
const OVERFLOW_NOTE_TOKENS = ['count', 'group', 'tab'];
const FALLBACK_TOKENS = ['title'];
const SECTION_TEXT_FIELDS = ['heading', 'intro', 'defaultMarkdown'];

export function scanContentStrings(body: unknown, options: { allowHttpsLinksOnly?: boolean } = {}): ContentIssue[] {
  const issues: ContentIssue[] = [];
  walkStrings(body, '', (text, path) => {
    for (const rule of FORBIDDEN_CONTENT_PATTERNS) {
      if (rule.pattern.test(text)) issues.push({ code: rule.code, path, message: rule.message, severity: 'error' });
    }
    if (options.allowHttpsLinksOnly !== false) {
      for (const m of text.matchAll(MARKDOWN_LINK)) {
        const url = m[1]!;
        if (/^(https:\/\/|#|mailto:)/i.test(url)) continue;
        if (/^\{[a-zA-Z_]+\}$/.test(url)) continue;
        issues.push({ code: 'insecure_link', path, message: `links must be https (found ${url.slice(0, 40)})`, severity: 'error' });
      }
    }
  });
  return issues;
}

function checkVariables(type: TemplateType, body: Record<string, unknown>, issues: ContentIssue[]): void {
  const fields = VARIABLE_FIELDS[type];
  if (!fields) return;
  const version = typeof body.variablesSchemaVersion === 'number' ? body.variablesSchemaVersion : null;
  const allowed = version !== null ? variablesFor(type as VariableSchemaType, version) : null;
  if (!allowed) {
    issues.push({ code: 'variable_schema_version', path: 'variablesSchemaVersion', message: `unknown variable schema version ${String(version)} for ${type}`, severity: 'error' });
    return;
  }
  const check = (text: unknown, path: string, permitted: readonly string[]): void => {
    if (typeof text !== 'string') return;
    for (const m of text.matchAll(VARIABLE_PATTERN)) {
      const name = m[1]!;
      if (!permitted.includes(name)) issues.push({ code: 'unknown_variable', path, message: `{${name}} is not defined in variable schema v${version}`, severity: 'error' });
    }
  };
  for (const field of fields) {
    const value = body[field];
    if (field === 'askLineTemplate') check(value, field, ASK_LINE_TOKENS);
    else if (field === 'overflowNote') check(value, field, OVERFLOW_NOTE_TOKENS);
    else if (field === 'missingAskFallback') {
      if (value && typeof value === 'object') for (const [k, v] of Object.entries(value as Record<string, unknown>)) check(v, `${field}.${k}`, FALLBACK_TOKENS);
    } else if (field === 'sections') {
      if (Array.isArray(value)) value.forEach((section, i) => { if (section && typeof section === 'object') for (const k of SECTION_TEXT_FIELDS) check((section as Record<string, unknown>)[k], `sections[${i}].${k}`, allowed); });
    } else if (Array.isArray(value)) value.forEach((v, i) => check(v, `${field}[${i}]`, allowed));
    else check(value, field, allowed);
  }
}

function checkPatterns(type: TemplateType, body: Record<string, unknown>, issues: ContentIssue[]): void {
  if (type !== 'matching_rule' || !Array.isArray(body.rules)) return;
  body.rules.forEach((rule, i) => {
    const source = (rule as { namePattern?: unknown }).namePattern;
    if (typeof source !== 'string') return;
    if (/\(\?[<=!]/.test(source) || source.length > 300) {
      issues.push({ code: 'invalid_pattern', path: `rules[${i}].namePattern`, message: 'lookaround and very long patterns are not allowed', severity: 'error' });
      return;
    }
    try {
      new RegExp(source, 'i');
    } catch {
      issues.push({ code: 'invalid_pattern', path: `rules[${i}].namePattern`, message: 'pattern does not compile', severity: 'error' });
    }
  });
}

/** Validate one template body: schema, forbidden content, variables, patterns. */
export function validateTemplateContent(type: TemplateType, body: unknown): ContentValidation {
  const issues: ContentIssue[] = [];
  const schema = TEMPLATE_BODY_SCHEMAS[type];
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) issues.push({ code: 'schema', path: issue.path.map(String).join('.') || '(root)', message: issue.message, severity: 'error' });
  }
  issues.push(...scanContentStrings(body));
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    checkVariables(type, body as Record<string, unknown>, issues);
    checkPatterns(type, body as Record<string, unknown>, issues);
  }
  return { ok: issues.every((i) => i.severity !== 'error'), issues };
}

// ---------------------------------------------------------------------------------------------
// Bundle validation
// ---------------------------------------------------------------------------------------------

export function computeItemDigest(body: unknown): string {
  return digestJson(body);
}

/** manifest.digest is sha256 over the canonical JSON of the items (type, id, version, digest, schemaVersion, body). */
export function computeBundleDigest(items: readonly ContentBundleItem[]): string {
  const sorted = [...items].sort((a, b) => (a.type < b.type ? -1 : a.type > b.type ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return digestJson(sorted.map((i) => ({ type: i.type, id: i.id, version: i.version, digest: i.digest, schemaVersion: i.schemaVersion, body: i.body })));
}

/** Structural + content validation of a downloaded bundle (signature verification happens in the caller with jose). */
export function validateContentBundle(bundle: unknown): ContentValidation {
  const issues: ContentIssue[] = [];
  const parsed = ContentBundleSchema.safeParse(bundle);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) issues.push({ code: 'schema', path: issue.path.map(String).join('.') || '(root)', message: issue.message, severity: 'error' });
    return { ok: false, issues };
  }
  const b: ContentBundle = parsed.data;
  const seen = new Set<string>();
  b.items.forEach((item, i) => {
    const key = `${item.type}:${item.id}`;
    if (seen.has(key)) issues.push({ code: 'manifest_mismatch', path: `items[${i}]`, message: `duplicate item ${key}`, severity: 'error' });
    seen.add(key);
    if (computeItemDigest(item.body) !== item.digest) issues.push({ code: 'digest_mismatch', path: `items[${i}].digest`, message: `digest does not match body for ${key}`, severity: 'error' });
    if (item.schemaVersion > TEMPLATE_BODY_SCHEMA_VERSION[item.type]) issues.push({ code: 'schema', path: `items[${i}].schemaVersion`, message: `unsupported schema version ${item.schemaVersion} for ${item.type}`, severity: 'error' });
    const content = validateTemplateContent(item.type, item.body);
    for (const issue of content.issues) issues.push({ ...issue, path: `items[${i}].body.${issue.path}` });
  });
  const manifestRefs = new Set(b.manifest.items.map((r) => `${r.type}:${r.id}:${r.version}:${r.digest}`));
  const itemRefs = new Set(b.items.map((r) => `${r.type}:${r.id}:${r.version}:${r.digest}`));
  for (const ref of manifestRefs) if (!itemRefs.has(ref)) issues.push({ code: 'manifest_mismatch', path: 'manifest.items', message: `manifest references ${ref} which is not in the bundle`, severity: 'error' });
  for (const ref of itemRefs) if (!manifestRefs.has(ref)) issues.push({ code: 'manifest_mismatch', path: 'items', message: `bundle item ${ref} is not referenced by the manifest`, severity: 'error' });
  if (computeBundleDigest(b.items) !== b.manifest.digest) issues.push({ code: 'digest_mismatch', path: 'manifest.digest', message: 'manifest digest does not match bundle items', severity: 'error' });
  return { ok: issues.every((i) => i.severity !== 'error'), issues };
}

/** Build a bundle item with a correct digest (used by seeds, fixtures and the admin publish flow). */
export function makeBundleItem(type: TemplateType, id: string, version: number, body: unknown): ContentBundleItem {
  const item = { type, id: TemplateIdSchema.parse(id), version, digest: computeItemDigest(body), schemaVersion: TEMPLATE_BODY_SCHEMA_VERSION[type], body };
  return ContentBundleItemSchema.parse(item);
}
